import axios from 'axios'
import httpEver from '../services/httpServiceEver'
import httpHealthCare from '../services/httpServiceHealthCare'

// ผู้ป่วยกับตารางนัดอยู่คนละฐาน: ค้นผู้ป่วยจากฝั่ง Ever OMA ด้วยเลขบัตรประชาชน
// แล้วแปลงเป็น patientId ของฝั่ง telemed ผ่าน AppUserIdentity ซึ่งจะถูกสร้างตอน
// ผู้ป่วยล็อกอินแอปครั้งแรก (backend/common/models/app-user.js -> loginWithEverOMAToken)
// ถ้าไม่มี mapping แปลว่ายังไม่เคยเข้าใช้งานแอป จึงยังนัดหมายให้ไม่ได้
// รับได้ทั้งเลขบัตรประชาชนและ HN ในช่องเดียว จึงค้นด้วย or ในคำขอเดียว
// (userInfoByPatientCid รับเฉพาะ cId เลยใช้ไม่ได้กับ HN)
export const findPatientByCidOrHn = async (keyword) => {
  const { data } = await httpHealthCare.get('/UserInfos', {
    params: {
      filter: {
        where: { or: [{ cId: keyword }, { hnId: keyword }] },
        fields: ['id', 'userId', 'cId', 'hnId', 'firstname', 'lastname', 'verifyId'],
        limit: 5,
      },
    },
  })
  return Array.isArray(data) ? data : []
}

export const getTelemedPatientId = async (everOmaId) => {
  const { data } = await httpEver.get(`/AppUserIdentities?filter[where][everOmaId]=${encodeURIComponent(everOmaId)}`)
  if (!Array.isArray(data) || !data.length) {
    return null
  }
  // ผูกซ้ำได้หลายแถว ใช้แถวล่าสุดแบบเดียวกับ ActionButtonSection.fetchAppUserIdentity
  return data[data.length - 1].appUserId
}

// แผนกในแอปผู้ป่วยมาจากสองแหล่งและแยกเป็นสองแท็บ "แพทย์" กับ "วิชาชีพอื่น"
// (app/screens/TeleSymptom/index.js) หน้านี้จึงต้องดึงทั้งคู่เหมือนกัน
export const getDoctorTypes = async () => {
  const { data } = await httpEver.get('/DoctorTypes')
  return data
}

export const getOtherTypes = async () => {
  const { data } = await httpEver.get('/OtherTypes')
  return data
}

export const getDoctorsByType = async (doctorTypeId) => {
  const { data } = await httpEver.get(`/DoctorTypes/${doctorTypeId}/appUsers?filter[order]=employeeId`)
  return data
}

export const getDoctorsByOtherType = async (otherTypeId) => {
  const { data } = await httpEver.get('/OtherTypes/getAppUserByOtherTypeId', {
    params: { otherTypeId },
  })
  return data
}

// { time: [[startMinute, endMinute], ...], minute } โดยนาทีนับจากต้นสัปดาห์ (จันทร์ 00:00)
// คืน null ได้ถ้าแพทย์ยังไม่ได้ตั้งตารางเวลา
export const getDoctorAvailableTime = async (doctorId) => {
  const { data } = await httpEver.get('/doctorAvailableTimes/filterType', {
    params: { doctorId, type: 'weekly' },
  })
  return data
}

export const getDoctorBookings = async (doctorId) => {
  const { data } = await httpEver.get('/Bookings/filterByDoctorId', {
    params: { doctorId },
  })
  return data
}

export const createBooking = async (payload) => {
  const { data } = await httpEver.post('/Bookings', payload)
  return data
}

export const getFcmTokensByOmaId = async (userId) => {
  const { data } = await httpEver.get('/userFcmTokens/getUserFcmTokenByUserId', {
    params: { userId },
  })
  return data
}

// ปฏิทินวันหยุดชุดเดียวกับที่แอปผู้ป่วยใช้ (app/screens/Appointment/hooks.js)
// ยิงด้วย axios ตรง ๆ เพื่อไม่ให้ interceptor แนบ access_token ของเจ้าหน้าที่ไปกับ Google
export const getThaiHolidays = async () => {
  const key = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY || 'AIzaSyBDLYwoxv5GtDACl-UE5M4aaIN77Q1MYno'
  const { data } = await axios.get(
    `https://www.googleapis.com/calendar/v3/calendars/th.th.official%23holiday%40group.v.calendar.google.com/events?key=${key}`
  )
  return (data.items || []).map((item) => item.start && item.start.date).filter(Boolean)
}

// ใช้โดยหน้า admin: ดูและลบนัดของผู้ป่วยรายหนึ่ง
// filterByPatientId include doctor และเรียงตาม admitTime DESC มาให้แล้ว
export const getPatientBookings = async (patientId) => {
  const { data } = await httpEver.get('/Bookings/filterByPatientId', {
    params: { patientId },
  })
  return Array.isArray(data) ? data : []
}

// ลบถาวร Booking.deleteById เปิดสิทธิ์ไว้ให้ role admin (common/models/booking.json)
export const deleteBooking = async (bookingId) => {
  const { data } = await httpEver.delete(`/Bookings/${encodeURIComponent(bookingId)}`)
  return data
}

// ผู้ป่วยที่ HIE ยังไม่ถูก trigger จะไม่มี hnId ใน OMA แต่ HIE มี HN อยู่แล้ว
// ดึงมาเติมได้ endpoint นี้เปิดให้ทุกคนเรียก (user-info.json -> acls $everyone)
export const getHieHnByCid = async (cId) => {
  const { data } = await httpHealthCare.get('/UserInfos/hiePatientInfo', {
    params: { cId },
  })
  // hn เป็น array ตาม hospCode ที่ผู้ป่วยเคยรักษา วชิรพยาบาลมี hospCode เดียว
  const first = data && Array.isArray(data.hn) ? data.hn[0] : null
  return first && first.hn ? String(first.hn) : null
}

// hnId เก็บเป็น string ในฐานข้อมูล (เช่น "660028318") ส่วน HIE คืนมาเป็นตัวเลข
export const updatePatientHn = async (userInfoId, hnId) => {
  const { data } = await httpHealthCare.patch(`/UserInfos/${encodeURIComponent(userInfoId)}`, {
    hnId: String(hnId),
  })
  return data
}
